import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DateRangePicker, RangeKeyDict, Range } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import addDays from 'date-fns/addDays'; // For default date range, specific path import
import { format as formatDateFns, startOfMonth, isSameMonth, addMonths } from 'date-fns'; // Removed unused parseDateISO, endOfMonth, added addMonths
import { API_URL } from '../config';
import './BookingPage.css';

// Apartment selection is removed
// const staticApartments = [...]; // This line was already commented, ensuring it or removing fully

const guestOptions = [1, 2, 3, 4];

interface CalendarDayInfo {
  isAvailable: boolean;
  minPrice: number | null;
  shouldCrossOut: boolean;
}

interface CalendarOverviewData {
  [date: string]: CalendarDayInfo;
}

interface AvailabilityResponse {
  isAvailable: boolean;
  totalPrice?: number;
  message?: string;
  error?: string;
  // apartmentId?: number; // If backend sends it for actual booking step
}

const BookingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [numberOfGuests, setNumberOfGuests] = useState<number>(1);
  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: 'selection',
    },
  ]);
  const [calendarOverview, setCalendarOverview] = useState<CalendarOverviewData>({});
  const [currentDisplayedMonths, setCurrentDisplayedMonths] = useState<Date[]>([startOfMonth(new Date()), startOfMonth(addMonths(new Date(), 1))]);
  const [availabilityInfo, setAvailabilityInfo] = useState<AvailabilityResponse | null>(null);
  const [selectedSuiteType, setSelectedSuiteType] = useState<'standard' | 'premium'>('standard');
  const [isLoading, setIsLoading] = useState<boolean>(false); // Combined loading state
  const [fetchedMonths, setFetchedMonths] = useState<Set<string>>(new Set()); // Track fetched YYYY-MM strings

  useEffect(() => {
    const fetchCalendarOverviewForMonths = async (monthsToFetch: Date[]) => {
      setIsLoading(true);
      let newOverviewData: CalendarOverviewData = {};
      let monthsActuallyFetched = 0;

      for (const monthDate of monthsToFetch) {
        const monthStr = formatDateFns(monthDate, 'yyyy-MM');
        if (fetchedMonths.has(monthStr)) {
          continue; // Already fetched or fetching this month
        }

        try {
          // console.log(`Fetching overview for month: ${monthStr} and type: ${selectedSuiteType}`);
          const response = await fetch(`${API_URL}/api/pricing/calendar-overview?month=${monthStr}&suiteType=${selectedSuiteType}`);
          if (!response.ok) {
            console.error(`Failed to fetch calendar overview for ${monthStr}: ${response.statusText}`);
            // Optionally skip this month or handle error more gracefully
            continue;
          }
          const data: Array<{ date: string; isAvailable: boolean; minPrice: number | null; shouldCrossOut: boolean }> = await response.json();
          data.forEach(item => {
            newOverviewData[item.date] = {
              isAvailable: item.isAvailable,
              minPrice: item.minPrice,
              shouldCrossOut: item.shouldCrossOut
            };
          });
          setFetchedMonths(prev => new Set(prev).add(monthStr));
          monthsActuallyFetched++;
        } catch (error) {
          console.error(`Error fetching calendar overview for ${monthStr}:`, error);
        }
      }
      if (monthsActuallyFetched > 0) {
        setCalendarOverview(prevOverview => ({ ...prevOverview, ...newOverviewData }));
      }
      setIsLoading(false);
    };

    // Determine which of the currentDisplayedMonths haven't been fetched yet
    const monthsToFetchNow = currentDisplayedMonths.filter(m => !fetchedMonths.has(formatDateFns(m, 'yyyy-MM')));
    if (monthsToFetchNow.length > 0) {
      fetchCalendarOverviewForMonths(monthsToFetchNow);
    }

  }, [currentDisplayedMonths, fetchedMonths, selectedSuiteType]);

  const handleSuiteTypeChange = (type: 'standard' | 'premium') => {
    setSelectedSuiteType(type);
    setFetchedMonths(new Set()); // Force re-fetch the calendar info for the new type
    setCalendarOverview({});
    setAvailabilityInfo(null);
  }

  const getDayInfo = useCallback((day: Date): CalendarDayInfo | null => {
    const dateStr = formatDateFns(day, 'yyyy-MM-dd');
    return calendarOverview[dateStr] || null;
  }, [calendarOverview]);

  const renderDayContents = (day: Date) => {
    const dayNumber = formatDateFns(day, 'd');
    const dayInfo = getDayInfo(day);

    let isSelected = false;
    if (dateRange[0] && dateRange[0].startDate && dateRange[0].endDate) {
      const startDate = dateRange[0].startDate;
      const endDate = dateRange[0].endDate;
      const currentDay = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      const rangeStartDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const rangeEndDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      if (currentDay >= rangeStartDay && currentDay <= rangeEndDay) {
        isSelected = true;
      }
    }

    const price = dayInfo?.minPrice;
    const isVisuallyUnavailable = !isSelected && (dayInfo?.shouldCrossOut ?? false);

    return (
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center p-1 pt-1.5 transition-all ${isVisuallyUnavailable ? 'opacity-30 bg-[url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIi8+CjxwYXRoIGQ9Ik0tMSwxbDIsLTIgTTEsNWw0LC00IE0zLDlsMiwtMiIgc3Ryb2tlPSIjZTBlMGUwIiBzdHJva2Utd2lkdGg9IjEiLz4KPC9zdmc+")]' : 'hover:bg-primary/5'} ${isSelected ? '!text-white' : ''}`}
      >
        <span className={`text-center font-label text-sm leading-none mt-1 ${isSelected ? 'text-white font-bold' : 'text-on-surface'}`} >{dayNumber}</span>
        {price != null ? (
          <span className={`text-center font-body text-[10px] mt-1 block ${isSelected ? 'text-white/90' : 'text-primary'}`} >€{Math.round(Number(price))}</span>
        ) : (
          !isVisuallyUnavailable && <span className={`text-center font-body text-[10px] mt-1 block opacity-50 ${isSelected ? 'text-white/50' : 'text-outline-variant'}`} >—</span>
        )}
      </div>
    );
  };

  const handleDateChange = (rangesByKey: RangeKeyDict) => {
    const newSelection = rangesByKey.selection;
    setDateRange([newSelection]);
    setAvailabilityInfo(null);
    // Note: currentDisplayedMonths is now primarily updated by onShownDateChange
  };

  const handleShownDateChange = (date: Date) => {
    // This function is called when the user navigates months in the DateRangePicker
    const firstVisibleMonth = startOfMonth(date);
    const secondVisibleMonth = startOfMonth(addMonths(firstVisibleMonth, 1));
    setCurrentDisplayedMonths([firstVisibleMonth, secondVisibleMonth]);
  };

  const handleGuestChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setNumberOfGuests(parseInt(event.target.value, 10));
    setAvailabilityInfo(null);
  };

  const checkAvailabilityAndPrice = async () => {
    if (!dateRange[0].startDate || !dateRange[0].endDate) return;

    setIsLoading(true);
    setAvailabilityInfo(null);
    const startDateStr = formatDateFns(dateRange[0].startDate, 'yyyy-MM-dd');
    const endDateStr = formatDateFns(dateRange[0].endDate, 'yyyy-MM-dd');

    try {
      // Call the new general availability endpoint
      const response = await fetch(`${API_URL}/api/bookings/check-availability?startDate=${startDateStr}&endDate=${endDateStr}&numGuests=${numberOfGuests}&suiteType=${selectedSuiteType}`);
      const data: AvailabilityResponse = await response.json();
      if (!response.ok) {
        // Use message from backend if available, otherwise a generic one
        throw new Error(data.message || 'Failed to check availability');
      }
      setAvailabilityInfo(data);
    } catch (error: any) {
      console.error("Error checking availability/price:", error);
      setAvailabilityInfo({ isAvailable: false, error: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceed = () => {
    if (availabilityInfo && availabilityInfo.isAvailable && dateRange[0].startDate && dateRange[0].endDate) {
      navigate('/booking/finalize', {
        state: {
          startDate: dateRange[0].startDate,
          endDate: dateRange[0].endDate,
          numGuests: numberOfGuests,
          totalPrice: availabilityInfo.totalPrice,
          suiteType: selectedSuiteType 
        }
      });
    }
  };

  return (
    <div className="bg-surface text-on-surface font-body selection:bg-secondary-container selection:text-on-secondary-container min-h-screen">
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-16">
        <div className="mb-16">
          <span className="font-label uppercase tracking-[0.2em] text-secondary font-bold text-xs mb-4 block">{t('bookingPage.heroLabel')}</span>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-light text-primary tracking-tight">{t('bookingPage.title').split(' ')[0]} {t('bookingPage.title').split(' ')[1]} <span className="italic font-normal">{t('bookingPage.title').split(' ').slice(2).join(' ')}</span></h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mt-16">
          {/* Left Column */}
          <div className="lg:col-span-8 space-y-12">
            
            <section>
              <h3 className="font-headline text-2xl text-primary mb-6">{t('bookingPage.suitesTitle') || 'Select Suite'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div 
                  onClick={() => handleSuiteTypeChange('standard')}
                  className={`border-2 rounded-xl p-4 cursor-pointer relative overflow-hidden group transition-all ${selectedSuiteType === 'standard' ? 'bg-surface-container-low border-primary shadow-md' : 'bg-surface-container-lowest border-outline-variant/30 hover:border-primary/50 opacity-80 mix-blend-luminosity hover:mix-blend-normal'}`}
                >
                  {selectedSuiteType === 'standard' && <div className="absolute top-2 right-2 w-3 h-3 bg-primary rounded-full z-10"></div>}
                  <div className="aspect-[4/3] rounded-lg overflow-hidden mb-4 bg-surface-container relative">
                    <img alt="Standard Suite (Sea View)" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src="/images/gallery/2/IMG_9786.jpg"/>
                  </div>
                  <h4 className="font-headline text-lg text-primary">Standard Suite (Sea View)</h4>
                  <p className="text-xs text-on-surface-variant mt-1">Comfortable apartment featuring a beautiful sea view. Ideal for up to 4 guests.</p>
                </div>
                
                <div 
                  onClick={() => handleSuiteTypeChange('premium')}
                  className={`border-2 rounded-xl p-4 cursor-pointer relative overflow-hidden group transition-all ${selectedSuiteType === 'premium' ? 'bg-surface-container-low border-primary shadow-md' : 'bg-surface-container-lowest border-outline-variant/30 hover:border-primary/50 opacity-80 mix-blend-luminosity hover:mix-blend-normal'}`}
                >
                  {selectedSuiteType === 'premium' && <div className="absolute top-2 right-2 w-3 h-3 bg-primary rounded-full z-10"></div>}
                  <div className="aspect-[4/3] rounded-lg overflow-hidden mb-4 bg-surface-container relative">
                    <img alt="Premium Suite (Private Garden)" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src="/images/gallery/prizemlje/IMG_9826.jpg"/>
                  </div>
                  <h4 className="font-headline text-lg text-primary">Premium Suite (Private Garden)</h4>
                  <p className="text-xs text-on-surface-variant mt-1">Spacious premium suite offering a private garden level access. Ideal for up to 4 guests.</p>
                </div>
              </div>
            </section>

            {/* 2. Calendar Block */}
            <section>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
                <h3 className="font-headline text-xl md:text-2xl text-primary">{t('bookingPage.selectDatesTitle')}</h3>
                <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs font-label uppercase tracking-widest text-on-surface-variant">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-surface-container-high border border-outline-variant/50"></span>
                    <span>{t('bookingPage.available')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-secondary"></span>
                    <span>{t('bookingPage.selected')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-surface-container-lowest border border-outline-variant/10 opacity-50 relative overflow-hidden">
                      <span className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIi8+CjxwYXRoIGQ9Ik0tMSwxbDIsLTIgTTEsNWw0LC00IE0zLDlsMiwtMiIgc3Ryb2tlPSIjZTBlMGUwIiBzdHJva2Utd2lkdGg9IjEiLz4KPC9zdmc+')] opacity-20"></span>
                    </span>
                    <span>{t('bookingPage.booked')}</span>
                  </div>
                </div>
              </div>

              <div className="bg-surface-container-low rounded-xl p-2 md:p-6 shadow-sm border border-outline-variant/10">
                {/* Use the exact DateRangePicker component from the existing code */}
                <div className="full-width-calendar overflow-x-auto">
                  <DateRangePicker
                    onChange={handleDateChange}
                    moveRangeOnFirstSelection={false}
                    months={window.innerWidth > 768 ? 2 : 1}
                    ranges={dateRange}
                    direction="horizontal"
                    preventSnapRefocus={true}
                    calendarFocus="backwards"
                    className="date-range-picker-custom"
                    minDate={new Date()}
                    dayContentRenderer={renderDayContents}
                    onShownDateChange={handleShownDateChange}
                    inputRanges={[]}
                    staticRanges={[]}
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Right Column: Sticky Summary */}
          <div className="lg:col-span-4">
            <div className="bg-surface-container-low rounded-xl p-8 sticky top-32 shadow-[0_10px_40px_rgba(27,28,25,0.03)] border border-outline-variant/10">
              <h3 className="font-headline text-2xl text-primary mb-6 border-b border-surface-container-highest pb-4">{t('bookingPage.summaryTitleLarge')}</h3>
              
              <div className="space-y-6 flex-grow">
                <div>
                  <label className="font-label text-[10px] uppercase tracking-wider text-on-surface-variant font-bold ml-1 mb-2 block">{t('bookingPage.guestsLabel')}</label>
                  <select
                    id="guest-select"
                    value={numberOfGuests}
                    onChange={handleGuestChange}
                    className="w-full bg-surface-container-lowest border border-outline-variant/20 focus:ring-1 focus:ring-primary focus:border-primary rounded-lg p-3 transition-all text-primary font-body appearance-none cursor-pointer"
                  >
                    {guestOptions.map(num => (
                      <option key={num} value={num}>{num} {t(num === 1 ? 'bookingPage.guestSingular' : 'bookingPage.guestPlural')}</option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface-container-lowest p-4 rounded-lg border border-outline-variant/10">
                    <span className="font-label text-[10px] uppercase tracking-wider text-on-surface-variant block mb-1">{t('bookingPage.checkIn')}</span>
                    <span className="font-headline text-primary block truncate">
                      {dateRange[0].startDate ? dateRange[0].startDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }) : t('bookingPage.selectDate')}
                    </span>
                  </div>
                  <div className="bg-surface-container-lowest p-4 rounded-lg border border-outline-variant/10">
                    <span className="font-label text-[10px] uppercase tracking-wider text-on-surface-variant block mb-1">{t('bookingPage.checkOut')}</span>
                    <span className="font-headline text-primary block truncate">
                      {dateRange[0].endDate && dateRange[0].startDate !== dateRange[0].endDate ? dateRange[0].endDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }) : t('bookingPage.selectDate')}
                    </span>
                  </div>
                </div>

                <div className="border-t border-surface-container-highest pt-6 my-6">
                  <div className="flex justify-between items-end mb-2">
                    <span className="font-body text-on-surface-variant">{t('bookingPage.selectedSuite')}</span>
                    <span className="font-body text-primary font-bold">{selectedSuiteType === 'standard' ? 'Standard Suite (Sea View)' : 'Premium Suite (Private Garden)'}</span>
                  </div>
                </div>

                <div className="bg-surface-container rounded-lg p-4 mb-6 border border-outline-variant/10">
                  <div className="flex gap-3">
                    <span className="material-symbols-outlined material-symbols-rounded text-secondary text-xl">info</span>
                    <div>
                      <h4 className="font-headline text-sm text-primary leading-none mt-1">{t('bookingPage.minStayTitle', { defaultValue: 'Minimum Stay Requirements' })}</h4>
                      <p className="font-body text-[11px] text-on-surface-variant mt-2 leading-relaxed">
                        {t('bookingPage.minStayDesc', { defaultValue: 'Please note: Bookings during Peak and Summer seasons require a minimum of 7 nights. Pre and Post season bookings require a minimum of 5 nights.' })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Calculation Area */}
              <div className="mt-8 space-y-4">
                <button
                  onClick={checkAvailabilityAndPrice}
                  disabled={isLoading}
                  className="w-full border border-primary text-primary hover:bg-primary/5 active:bg-primary/10 px-6 py-4 rounded-xl font-label text-sm font-bold uppercase tracking-widest transition-all text-center disabled:opacity-50"
                >
                  {isLoading ? t('bookingPage.reserveButton.calculating') : t('bookingPage.checkAvailabilityButton')}
                </button>

                {availabilityInfo && (
                  <div className="mt-6 pt-6 border-t border-surface-container-highest text-center">
                    {availabilityInfo.error && (
                      <p className="text-secondary font-bold mb-4">{availabilityInfo.error || availabilityInfo.message}</p>
                    )}
                    {availabilityInfo.isAvailable === false && !availabilityInfo.error && (
                      <p className="text-secondary font-bold mb-4">{availabilityInfo.message || t('bookingPage.unavailableMessage')}</p>
                    )}
                    {availabilityInfo.isAvailable === true && (
                      <>
                        <p className="text-primary font-bold mb-2">{availabilityInfo.message || t('bookingPage.availableMessageGeneral')}</p>
                        {availabilityInfo.totalPrice !== undefined && (
                          <div className="text-4xl font-headline text-primary mb-6">
                            €{availabilityInfo.totalPrice.toFixed(2)}
                            <span className="text-sm font-body text-on-surface-variant block mt-1">{t('bookingPage.totalPriceCalculated')}</span>
                          </div>
                        )}
                        <button 
                          className="w-full bg-gradient-to-br from-[#002045] to-[#1a365d] text-white px-6 py-4 rounded-xl font-label text-sm font-bold uppercase tracking-widest shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
                          onClick={handleProceed}
                        >
                          {t('bookingPage.reserveButton.proceed')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookingPage; 